import type {
  BacktestEvent,
  BacktestResult,
  attributeFxAdjustedPriceChange,
  ForwardReversionStudy,
  MatifDecomposition,
  MetricResult,
  SeasonalPanelAnalysis,
  UnitRootEstimate,
  EngleGrangerEstimate,
  HalfLifeEstimate,
  WindowSensitivityResult,
} from "../analytics";
import type {
  AnalysisConfig,
  DataCoverage,
  Instrument,
  PairPreset as DataPairPreset,
} from "../data";
import type { DisplayMetric, ISODate, PairPresetId } from "../domain";
import type {
  ContractChoice,
  ContractPairSelection,
  PairDashboardFixture,
} from "../features/dashboard/types";

export type AnalysisDirection = "both" | "leg-a-rich" | "leg-a-cheap";
export type AnalysisSignalType = "local-z" | "seasonal-z" | "composite";
export type AnalysisSizing = "fixed-ratio" | "notional-neutral" | "beta-neutral";

export interface DashboardAnalysisControls {
  readonly window: number;
  readonly metric: "primary" | DisplayMetric;
  readonly signalType: AnalysisSignalType;
  readonly direction: AnalysisDirection;
  readonly entryZ: number;
  readonly exitZ: number;
  readonly sizing: AnalysisSizing;
  readonly quality: {
    readonly excludeLowLiquidity: boolean;
    readonly excludeSourceWarnings: boolean;
  };
}

export const DEFAULT_DASHBOARD_ANALYSIS_CONTROLS: DashboardAnalysisControls = {
  window: 60,
  metric: "primary",
  signalType: "local-z",
  direction: "both",
  entryZ: 2,
  exitZ: 0.5,
  sizing: "beta-neutral",
  quality: {
    excludeLowLiquidity: true,
    excludeSourceWarnings: true,
  },
};

export type DashboardMappingRequest =
  | { readonly mode: "auto" }
  | {
      readonly mode: "manual";
      readonly legAContractId: string;
      readonly legBContractId: string;
    };

export interface DashboardAnalysisRequest {
  readonly pairPresetId: PairPresetId;
  /** Exchange-business-date cutoff. Later settlements and revisions are excluded. */
  readonly asOf: ISODate;
  readonly mapping: DashboardMappingRequest;
  readonly controls: DashboardAnalysisControls;
}

export interface ApplicationCatalog {
  readonly instruments: readonly Instrument[];
  readonly pairPresets: readonly DataPairPreset[];
  readonly analysisConfigs: readonly AnalysisConfig[];
}

export interface DashboardPairCatalogEntry {
  readonly id: PairPresetId;
  readonly shortLabel: string;
  readonly label: string;
  readonly description: string;
  readonly autoContracts: ContractPairSelection | null;
  readonly manualLegAChoices: readonly ContractChoice[];
  readonly manualLegBChoices: readonly ContractChoice[];
}

export interface ApplicationDataProvenance {
  readonly mode: "demo" | "production";
  readonly sourceId: string;
  readonly generatedAt: string;
  readonly declaredAsOf: ISODate;
  readonly requestAsOf: ISODate;
  /** Null when the source cannot reproduce a historical ingestion/revision cutoff. */
  readonly inputCutoffTimestamp: string | null;
  /** Whether inputs reproduce the information set then available or today's view of history. */
  readonly knowledgeScope: "point-in-time" | "current-knowledge";
  readonly synthetic: boolean;
  readonly coverage: DataCoverage;
  readonly analysisConfigId: string;
  readonly analysisConfigVersion: string;
}

export interface DashboardBacktestEvent extends BacktestEvent {
  /** Date through which every model input used by this execution was observable. */
  readonly modelParametersAsOf: ISODate | null;
}

export type DashboardBacktestResult = Omit<BacktestResult, "events"> & {
  readonly events: readonly DashboardBacktestEvent[];
};

export interface DashboardAnalysisDiagnostics {
  readonly analysisConfig: {
    readonly id: string;
    readonly version: string;
  };
  readonly actualContracts: {
    readonly legAContractId: string;
    readonly legBContractId: string;
    readonly legAContract: string;
    readonly legBContract: string;
  };
  readonly selectedMetric: DisplayMetric;
  readonly primaryStatisticalMetric: DisplayMetric;
  readonly currentEpisode: {
    readonly episodeId: string;
    readonly dates: readonly ISODate[];
    readonly eligibleObservations: number;
    readonly excludedObservations: number;
    readonly latestDate: ISODate;
  };
  readonly currentLocalZ: number | null;
  readonly currentSeasonalZ: number | null;
  readonly adfResidual: MetricResult<UnitRootEstimate>;
  readonly engleGranger: MetricResult<EngleGrangerEstimate>;
  readonly halfLife: MetricResult<HalfLifeEstimate>;
  readonly seasonality: SeasonalPanelAnalysis;
  readonly windowSensitivity: WindowSensitivityResult;
  readonly backtest: DashboardBacktestResult;
  readonly episodeContractYears: Readonly<Record<string, number>>;
  readonly forwardStudy: ForwardReversionStudy;
  readonly matifDecomposition: MetricResult<MatifDecomposition> | null;
  readonly matifFxAttribution: MetricResult<ReturnType<typeof attributeFxAdjustedPriceChange>> | null;
  readonly matifMaturity: {
    readonly template: string;
    readonly gapMonths: number;
    readonly quality: "matched" | "maturity-gap";
    readonly detail: string;
  } | null;
  readonly invokedAnalytics: readonly string[];
}

export interface DashboardAnalysisReady {
  readonly status: "ready";
  readonly fixture: PairDashboardFixture;
  readonly diagnostics: DashboardAnalysisDiagnostics;
  readonly provenance: ApplicationDataProvenance;
}

export interface FormalWalkForwardUnavailable {
  readonly status: "not-available";
  readonly reason: "historical-point-in-time-unavailable";
  readonly detail: string;
}

/**
 * Diagnostics that are defensible from the latest known actual-contract data,
 * but cannot support historical signal execution or P/L.
 */
export type DashboardCurrentKnowledgeDiagnostics = DashboardAnalysisDiagnostics & {
  readonly analysisScope: "latest-release-research";
  readonly formalWalkForward: FormalWalkForwardUnavailable;
};

export interface DashboardCurrentKnowledgeReady {
  readonly status: "current-knowledge-ready";
  readonly fixture: PairDashboardFixture;
  readonly diagnostics: DashboardCurrentKnowledgeDiagnostics;
  readonly provenance: ApplicationDataProvenance;
}

export interface DashboardAnalysisUnavailable {
  readonly status: "not-available";
  readonly pairPresetId: PairPresetId;
  readonly reason:
    | "contract-not-found"
    | "contract-mismatch"
    | "contract-data-unavailable"
    | "data-coverage-unavailable"
    | "historical-point-in-time-unavailable"
    | "insufficient-eligible-observations"
    | "unsupported-manual-template";
  readonly detail: string;
  readonly provenance: ApplicationDataProvenance;
}

export type DashboardAnalysisResult = DashboardAnalysisReady | DashboardAnalysisUnavailable;

export type DashboardCurrentKnowledgeResult =
  | DashboardCurrentKnowledgeReady
  | DashboardAnalysisUnavailable;
